/* arrBreakdown.js -- THE ONE "ARR BY REVENUE MODULE" BUILDER (W-085, 2026-10-01).
 *
 * WHY THIS EXISTS: the breakdown of ARR by revenue module (AE Quota, Company Quota, each misc line counted in ARR) was
 * derived THREE times -- the KPI table, the KPI chart and Scenarios' "Ending ARR" sub-rows -- with no shared code. The
 * KPI copies took their row set from the engine's gate (kpiARRConfig); Scenarios re-derived the gate inline. They agreed
 * on every tenant, but one engine edit to the gate made them disagree on screen (diffs/group3-phase0.txt, cqgate36).
 *
 * THE RULES, which are the ones the three copies already used:
 *   - the ROW SET comes from kpiARRConfig ONLY (runFullModel builds it): hasAEQuota, hasCQ, miscLines.
 *   - a FORECAST month (m > cutoff, with a kpiARR entry) reads kpiARR[m]: aeQuota, companyQuota, misc[id].amount.
 *   - a HISTORICAL month (m <= cutoff) reads each module's existing_arr grid, shown only where it is above zero.
 *   - hist[m] carries the two grid values for EVERY month (the KPI chart stacks them, zeros included).
 * Display only: kpiARR never feeds isData / bsData. Labels are the callers' (moduleLineLabel is the label owner).
 */

import { aeArrSeed } from './existingBook';   // W-154 (a): the AE row is "estimated" exactly when the engine's seed guessed

const r2 = n => Math.round(n * 100) / 100;

export function arrBreakdown({ kpiARR, kpiARRConfig, aeqSettings, cqSettings, cutoff, months }) {
  const cfg = kpiARRConfig || {};
  const k = kpiARR || {};
  const rows = [];
  /* W-154 (a), 2026-10-05: `estimated` -- the AE forecast ARR rolls from revenue x 12 because no ARR at the close was entered
     (utils/existingBook.js aeArrSeed, the rule runFullModel's seed reads). The callers label the row "estimated" (rule 92). */
  if (cfg.hasAEQuota) rows.push({ id: 'ae_quota', kind: 'ae_quota', byMonth: {}, estimated: aeArrSeed(aeqSettings, cutoff).estimated });
  if (cfg.hasCQ) rows.push({ id: 'company_quota', kind: 'company_quota', byMonth: {} });
  (cfg.miscLines || []).forEach(ml => rows.push({ id: 'misc_' + ml.id, kind: 'misc', miscId: ml.id, name: ml.name, byMonth: {} }));
  const hist = {};
  (months || []).forEach(m => {
    const aeH = r2(parseFloat(aeqSettings?.existing_arr?.[m]) || 0);
    const cqH = r2(parseFloat(cqSettings?.existing_arr?.[m]) || 0);
    hist[m] = { ae: aeH, cq: cqH };
    const obj = k[m];
    if (m > cutoff && obj) {
      rows.forEach(r => {
        r.byMonth[m] = r.kind === 'ae_quota' ? (obj.aeQuota || 0)
          : r.kind === 'company_quota' ? (obj.companyQuota || 0)
          : (obj.misc?.[r.miscId]?.amount || 0);
      });
    } else if (m <= cutoff) {
      rows.forEach(r => {
        if (r.kind === 'ae_quota' && aeH > 0) r.byMonth[m] = aeH;
        if (r.kind === 'company_quota' && cqH > 0) r.byMonth[m] = cqH;
      });
    }
  });
  return { rows, hist };
}

/** One row's value in one month, 0 when the row is not in the set or the month has no value. */
export function arrRowValue(breakdown, rowId, m) {
  const row = ((breakdown && breakdown.rows) || []).find(r => r.id === rowId);
  return (row && row.byMonth[m]) || 0;
}

/* NO ARR (2026-11-03, Brian: when a company has no ARR, the Total ARR tile and chart become Monthly Revenue;
 * diffs/runway-arr-tiles-build.txt). The definition reads the page's OWN ARR series: a company has no ARR when that series
 * is zero or empty in EVERY month of the window, history and forecast. arrNum reads a month's value as the series stores
 * it -- a number, or (the KPI Dashboard's merge keeps a kpiARR entry whose total is 0 as the entry itself) an object's total. */
export function arrNum(v) {
  if (v && typeof v === 'object') return Number(v.total) || 0;
  return Number(v) || 0;
}
export function hasAnyArr(series, months) {
  const s = series || {};
  return (months || []).some(m => arrNum(s[m]) !== 0);
}
